/**
 * Intelligence → Insights → ANTICIPATE, on the wire.
 *
 * A TEMPORARY SEAM, and it says so for the same reason `insightsClient.ts`
 * once did. The canonical home for this is
 * `packages/api-client/src/forecasts.ts` re-exported from that package's
 * `index.ts`, and both of those are integrator-owned — `index.ts` is the
 * highest-churn shared file in the repository. The lane report names the module
 * and the one export line; when they land, this file collapses to a re-export
 * exactly as its neighbour did, and none of its importers move.
 *
 * ---------------------------------------------------------------------------
 * FOUR-STATE DISCIPLINE ON THE WIRE
 * ---------------------------------------------------------------------------
 * `ForecastDisclosure` is a discriminated union, never a nullable label:
 *
 *   shown     the caller may read this entity; `label` is its name.
 *   withheld  it exists and policy forbids naming it here.
 *   absent    the row it named is gone.
 *
 * A caller who does not reach a forecast's SUBJECT never receives the forecast
 * at all, so refused-for-scope is answered one level up — by `withheld` on the
 * list and by a 404 from the detail route, which is byte-identical to the 404
 * for a forecast that never existed.
 */

import { get } from '../../lib/api.js';

export type ForecastType = 'credential-requirement-lapse';
export type ForecastTransitionReason = 'credential_expiry' | 'requirement_takes_effect';
export type ForecastBasis = 'dated_obligation';
export type ForecastLikelihood = 'certain_absent_action' | 'undetermined';
export type ForecastOutcome = 'pending' | 'realized' | 'averted' | 'withdrawn';

export type ForecastDisclosure =
  { disclosure: 'shown'; label: string } | { disclosure: 'withheld' } | { disclosure: 'absent' };

export interface ForecastSummary {
  id: string;
  forecastId: string;
  forecastType: ForecastType;
  transitionReason: ForecastTransitionReason;
  basis: ForecastBasis;
  likelihood: ForecastLikelihood;
  /** Always present. A forecast that cannot say why it believes itself is unstorable. */
  likelihoodBasis: string;
  /** The action that would prove this wrong — which is the point of showing it. */
  falsifiedBy: string;
  horizonOn: string;
  observedOn: string;
  daysAhead: number;
  subjectKind: 'worker';
  subject: ForecastDisclosure & { id: string };
  credentialTypeLabel: ForecastDisclosure;
  programId: string | null;
  outcome: ForecastOutcome;
  outcomeOn: string | null;
  outcomeBasis: string | null;
}

export interface ForecastList {
  items: ForecastSummary[];
  /** The CALLER's total, narrowed before the page. Never the tenant's. */
  total: number;
  /** Rows policy withholds. Reported, never implied — `withheld` is not zero. */
  withheld: number;
  /** WHICH POPULATION the numbers above are over. Read it before quoting one. */
  scope: 'organization' | 'caseload';
}

export const forecasts = {
  list(params: { outcome?: ForecastOutcome | 'any'; limit?: number } = {}): Promise<ForecastList> {
    const query = new URLSearchParams();
    if (params.outcome !== undefined) query.set('outcome', params.outcome);
    if (params.limit !== undefined) query.set('limit', String(params.limit));
    const suffix = query.toString();
    return get<ForecastList>(`/api/insights/forecasts${suffix === '' ? '' : `?${suffix}`}`);
  },
  get(id: string): Promise<ForecastSummary> {
    return get<ForecastSummary>(`/api/insights/forecasts/${encodeURIComponent(id)}`);
  },
};
